import axios, { type AxiosResponse } from 'axios';
import { config } from 'shared/config';
import { primeSharedResource, takePrimedResource } from 'shared/api/readResource';
import { isLinkRead, linkManifest } from './linkReads';

type ManifestScope = { reportId: string; pageId: number; userId: string };

const manifestKey = ({ reportId, pageId, userId }: ManifestScope) => `visuals:page-shell-manifest:${reportId}:${pageId}:${userId}`;

/** Read a page's shell manifest; `page` is the numeric page id or the page UID. */
export const requestPageShellManifest = <T>(
    reportId: string, page: number | string, userId: string, signal?: AbortSignal,
): Promise<AxiosResponse<T>> => (
    // A page opened by public link has its shell from the link (`linkReads`).
    isLinkRead(reportId) ? Promise.resolve(linkManifest() as AxiosResponse<T>)
        : axios.get<T>(`${config.API_URL}/api/pages/visual-shell-manifest`, { params: { pageId: page, userId, reportId }, signal })
);

/** Read the shell manifest of the report's first page this user can see, before the workspace names it. */
export const requestInitialPageShellManifest = <T>(reportId: string, userId: string): Promise<AxiosResponse<T>> => (
    axios.get<T>(`${config.API_URL}/api/pages/visual-shell-manifest`, { params: { initialPage: 'first', userId, reportId } })
);

/**
 * Hand a page's shell manifest read to the canvas before it mounts (report open).
 * `pending` is a read already started for the same page (for example by its UID).
 */
export const prefetchPageShellManifest = (scope: ManifestScope, pending?: Promise<AxiosResponse<unknown>>): void => {
    void primeSharedResource(manifestKey(scope), () => pending ?? requestPageShellManifest(scope.reportId, scope.pageId, scope.userId));
};

/**
 * The canvas read: takes a prefetched manifest for the same page once (report
 * open), otherwise reads the current manifest. Never shares a read with another
 * consumer, so a read after a change always sees that change.
 */
export const loadPageShellManifest = <T>(scope: ManifestScope, signal?: AbortSignal): Promise<AxiosResponse<T>> => (
    takePrimedResource<AxiosResponse<T>>(manifestKey(scope))
        ?? requestPageShellManifest<T>(scope.reportId, scope.pageId, scope.userId, signal)
);
